<template>
  <div class="mx-auto px-4 py-4 w-[95%]">
    <!-- Шапка -->
    <div class="flex items-center justify-between mb-4">
      <div>
        <button type="button" @click="goBack" class="text-sm text-blue-600 hover:underline mb-1">← К реестру</button>
        <h1 class="text-lg font-semibold text-gray-800">Фр-УР-01. Форма оценки рисков. Версия 01</h1>
      </div>
      <span :class="statusClass(doc.status)" class="text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
        {{ statusLabel(doc.status) }}
      </span>
    </div>

    <div v-if="loading" class="text-sm text-gray-500">Загрузка...</div>

    <div v-else class="space-y-6">
      <!-- Шапка документа -->
      <section class="bg-white border border-gray-200 rounded-lg p-4 grid grid-cols-2 gap-4">
        <div>
          <label class="block text-sm text-gray-700 mb-1">Номер оценки рисков</label>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 h-[39px] leading-[39px]">{{ doc.number || '—' }}</span>
          <input
            v-else
            v-model="doc.number"
            type="text"
            placeholder="ОР-..."
            class="w-full border border-gray-300 rounded-lg px-3 text-sm h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label class="block text-sm text-gray-700 mb-1">Затронутый продукт</label>
          <div class="flex items-start gap-1">
            <div class="flex-1 min-w-0">
              <span v-if="isReadOnly" class="block text-sm text-gray-700 h-[39px] leading-[39px]">{{ doc.affected_product_name || '—' }}</span>
              <div v-else-if="!manualProduct" class="v-select-filter" style="width: 100%; flex: none;">
                <v-select
                  v-model="productSelected"
                  :options="productResults"
                  :filterable="false"
                  :loading="productLoading"
                  :get-option-label="productLabel"
                  label="name"
                  placeholder="Поиск по названию и артикулу"
                  @search="onProductSearch"
                  @open="() => onProductSearch('')"
                  @update:modelValue="onProductSelected"
                >
                  <template #option="o">
                    <span>{{ productLabel(o) }}</span>
                  </template>
                  <template #no-options>
                    <span v-if="productLoading">Загрузка...</span>
                    <span v-else>Ничего не найдено</span>
                  </template>
                  <template #list-footer>
                    <li v-if="productHasMore" class="px-3 py-2 text-center border-t">
                      <button
                        type="button"
                        @click.prevent.stop="loadMoreProducts"
                        :disabled="productLoading"
                        class="text-blue-600 hover:text-blue-800 disabled:text-gray-400"
                      >
                        {{ productLoading ? 'Загрузка...' : 'Загрузить ещё...' }}
                      </button>
                    </li>
                  </template>
                </v-select>
              </div>
              <input
                v-else
                v-model="doc.affected_product_name"
                type="text"
                placeholder="Ручной ввод"
                class="w-full border border-gray-300 rounded-lg px-3 text-sm h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              v-if="!isReadOnly"
              type="button"
              @click="toggleManualProduct"
              :title="manualProduct ? 'Выбрать из номенклатуры' : 'Ввести вручную'"
              class="flex-shrink-0 h-[39px] w-8 inline-flex items-center justify-center border border-gray-300 rounded text-gray-500 hover:bg-gray-50"
            >
              {{ manualProduct ? '☰' : '✎' }}
            </button>
          </div>
        </div>
        <div>
          <label class="block text-sm text-gray-700 mb-1">Дата</label>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 h-[39px] leading-[39px]">{{ assessmentDateDisplay }}</span>
          <input
            v-else
            :value="assessmentDateDisplay"
            type="text"
            disabled
            class="w-full border border-gray-300 rounded-lg px-3 text-sm bg-gray-100 h-[39px]"
          />
        </div>
        <div>
          <label class="block text-sm text-gray-700 mb-1">Ответственный</label>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 h-[39px] leading-[39px]">{{ creatorDisplay }}</span>
          <input
            v-else
            :value="creatorDisplay"
            type="text"
            disabled
            class="w-full border border-gray-300 rounded-lg px-3 text-sm bg-gray-100 h-[39px]"
          />
        </div>
        <div>
          <label class="block text-sm text-gray-700 mb-1">Источник документа</label>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 h-[39px] leading-[39px]">{{ originDisplay }}</span>
          <input
            v-else
            :value="originDisplay"
            type="text"
            disabled
            class="w-full border border-gray-300 rounded-lg px-3 text-sm bg-gray-100 h-[39px]"
          />
        </div>
        <!-- Привязка к изделию у оценки рисков — в шапке: строк по продуктам у документа нет -->
        <div class="col-span-2">
          <label class="block text-sm text-gray-700 mb-1">Привязка к изделию</label>
          <ProductBindingSelector
            v-model="binding"
            :read-only="isReadOnly"
            @product-picked="onBindingProductPicked"
          />
        </div>
        <div class="col-span-2">
          <label class="block text-sm text-gray-700 mb-1">Цель оценки рисков</label>
          <span v-if="isReadOnly" class="block text-sm text-gray-700 whitespace-pre-line">{{ doc.purpose || '—' }}</span>
          <textarea
            v-else
            v-model="doc.purpose"
            rows="2"
            class="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          ></textarea>
        </div>
      </section>

      <!-- Матрица рисков -->
      <section class="bg-white border border-gray-200 rounded-lg p-4">
        <h2 class="font-semibold text-gray-800 mb-3">Матрица рисков</h2>
        <div class="overflow-x-auto">
          <table class="w-full text-sm border border-gray-200 min-w-[1100px]">
            <thead class="bg-gray-50">
              <tr>
                <th rowspan="2" class="px-2 py-1 border text-center w-8">№</th>
                <th rowspan="2" class="px-2 py-1 border text-left">Процесс</th>
                <th rowspan="2" class="px-2 py-1 border text-left">Опасность</th>
                <th rowspan="2" class="px-2 py-1 border text-left">Последствия</th>
                <th colspan="3" class="px-2 py-1 border text-center">Оценка</th>
                <th rowspan="2" class="px-2 py-1 border text-left">Действия по управлению рисками</th>
                <th colspan="3" class="px-2 py-1 border text-center">Повторная оценка рисков после действий</th>
                <th rowspan="2" class="px-2 py-1 border text-left">Доп. действия по снижению риска</th>
                <th rowspan="2" class="px-2 py-1 border w-8"></th>
              </tr>
              <tr>
                <th class="px-1 py-1 border text-center w-10"><span :style="verticalHeaderStyle">Значимость</span></th>
                <th class="px-1 py-1 border text-center w-10"><span :style="verticalHeaderStyle">Вероятность</span></th>
                <th class="px-1 py-1 border text-center w-10"><span :style="verticalHeaderStyle">Ранг</span></th>
                <th class="px-1 py-1 border text-center w-10"><span :style="verticalHeaderStyle">Значимость</span></th>
                <th class="px-1 py-1 border text-center w-10"><span :style="verticalHeaderStyle">Вероятность</span></th>
                <th class="px-1 py-1 border text-center w-10"><span :style="verticalHeaderStyle">Ранг</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="rows.length === 0">
                <td colspan="13" class="px-2 py-2 text-gray-400 text-center border">Нет строк</td>
              </tr>
              <tr v-for="(row, index) in rows" :key="index">
                <td class="px-2 py-1 border text-center align-top text-gray-500">{{ index + 1 }}</td>
                <td class="border align-top"><span v-if="isReadOnly" class="block min-w-[140px] px-2 py-1 text-sm whitespace-pre-line">{{ row.process || '—' }}</span><textarea v-else v-model="row.process" rows="2" placeholder="Введите текст" class="w-full min-w-[140px] border-0 px-2 py-1 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-400"></textarea></td>
                <td class="border align-top"><span v-if="isReadOnly" class="block min-w-[140px] px-2 py-1 text-sm whitespace-pre-line">{{ row.hazard || '—' }}</span><textarea v-else v-model="row.hazard" rows="2" placeholder="Введите текст" class="w-full min-w-[140px] border-0 px-2 py-1 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-400"></textarea></td>
                <td class="border align-top"><span v-if="isReadOnly" class="block min-w-[140px] px-2 py-1 text-sm whitespace-pre-line">{{ row.consequences || '—' }}</span><textarea v-else v-model="row.consequences" rows="2" placeholder="Введите текст" class="w-full min-w-[140px] border-0 px-2 py-1 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-400"></textarea></td>
                <td class="border align-top"><span v-if="isReadOnly" class="block w-16 px-1 py-1 text-sm text-center">{{ row.significance === '' ? '—' : row.significance }}</span><select v-else v-model="row.significance" class="w-16 border-0 px-1 py-1 text-sm text-center bg-transparent focus:outline-none focus:ring-1 focus:ring-blue-500"><option value="">—</option><option v-for="n in scoreOptions" :key="n" :value="String(n)">{{ n }}</option></select></td>
                <td class="border align-top"><span v-if="isReadOnly" class="block w-16 px-1 py-1 text-sm text-center">{{ row.probability === '' ? '—' : row.probability }}</span><select v-else v-model="row.probability" class="w-16 border-0 px-1 py-1 text-sm text-center bg-transparent focus:outline-none focus:ring-1 focus:ring-blue-500"><option value="">—</option><option v-for="n in scoreOptions" :key="n" :value="String(n)">{{ n }}</option></select></td>
                <td class="border align-top p-0" :style="{ backgroundColor: cellRankColor(row, 'rank') }"><span v-if="isReadOnly" class="block w-16 px-1 py-1 text-sm text-center font-semibold">{{ row.rank === '' ? '—' : row.rank }}</span><select v-else v-model="row.rank" class="w-16 bg-transparent border-0 px-1 py-1 text-sm text-center font-semibold focus:outline-none focus:ring-1 focus:ring-blue-500"><option value="">—</option><option v-for="n in scoreOptions" :key="n" :value="String(n)">{{ n }}</option></select><select v-if="!isReadOnly" v-model="row.rank_color" class="w-full bg-transparent border-0 text-[10px] text-center px-0 pb-1 focus:outline-none focus:ring-1 focus:ring-blue-500"><option v-for="c in rankPalette" :key="c.value || 'auto'" :value="c.value">{{ c.title }}</option></select></td>
                <td class="border align-top"><span v-if="isReadOnly" class="block min-w-[140px] px-2 py-1 text-sm whitespace-pre-line">{{ row.control_actions || '—' }}</span><textarea v-else v-model="row.control_actions" rows="2" placeholder="Введите текст" class="w-full min-w-[140px] border-0 px-2 py-1 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-400"></textarea></td>
                <td class="border align-top"><span v-if="isReadOnly" class="block w-16 px-1 py-1 text-sm text-center">{{ row.re_significance === '' ? '—' : row.re_significance }}</span><select v-else v-model="row.re_significance" class="w-16 border-0 px-1 py-1 text-sm text-center bg-transparent focus:outline-none focus:ring-1 focus:ring-blue-500"><option value="">—</option><option v-for="n in scoreOptions" :key="n" :value="String(n)">{{ n }}</option></select></td>
                <td class="border align-top"><span v-if="isReadOnly" class="block w-16 px-1 py-1 text-sm text-center">{{ row.re_probability === '' ? '—' : row.re_probability }}</span><select v-else v-model="row.re_probability" class="w-16 border-0 px-1 py-1 text-sm text-center bg-transparent focus:outline-none focus:ring-1 focus:ring-blue-500"><option value="">—</option><option v-for="n in scoreOptions" :key="n" :value="String(n)">{{ n }}</option></select></td>
                <td class="border align-top p-0" :style="{ backgroundColor: cellRankColor(row, 're_rank') }"><span v-if="isReadOnly" class="block w-16 px-1 py-1 text-sm text-center font-semibold">{{ row.re_rank === '' ? '—' : row.re_rank }}</span><select v-else v-model="row.re_rank" class="w-16 bg-transparent border-0 px-1 py-1 text-sm text-center font-semibold focus:outline-none focus:ring-1 focus:ring-blue-500"><option value="">—</option><option v-for="n in scoreOptions" :key="n" :value="String(n)">{{ n }}</option></select><select v-if="!isReadOnly" v-model="row.re_rank_color" class="w-full bg-transparent border-0 text-[10px] text-center px-0 pb-1 focus:outline-none focus:ring-1 focus:ring-blue-500"><option v-for="c in rankPalette" :key="c.value || 'auto'" :value="c.value">{{ c.title }}</option></select></td>
                <td class="border align-top"><span v-if="isReadOnly" class="block min-w-[140px] px-2 py-1 text-sm whitespace-pre-line">{{ row.additional_actions || '—' }}</span><textarea v-else v-model="row.additional_actions" rows="2" placeholder="Введите текст" class="w-full min-w-[140px] border-0 px-2 py-1 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-blue-500 placeholder-gray-400"></textarea></td>
                <td class="border align-top text-center">
                  <button v-if="!isReadOnly" type="button" @click="removeRow(index)" class="text-red-600 hover:text-red-800 px-1" title="Удалить строку">✕</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="!isReadOnly" class="mt-3">
          <button type="button" @click="addRow" class="text-sm text-blue-600 hover:underline">+ Добавить строку</button>
        </div>
      </section>

      <!-- Утверждение -->
      <section class="bg-white border border-gray-200 rounded-lg p-4">
        <h2 class="font-semibold text-gray-800 mb-3">Утверждение</h2>
        <table class="w-full text-sm border border-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-2 py-1 text-left border-b">ФИО</th>
              <th class="px-2 py-1 text-left border-b w-1/3">Позиция</th>
              <th class="px-2 py-1 text-left border-b w-44">Дата одобрения</th>
              <th class="px-2 py-1 border-b w-10"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="approvers.length === 0">
              <td colspan="4" class="px-2 py-2 text-gray-400 text-center">Нет согласующих</td>
            </tr>
            <tr v-for="(approver, index) in approvers" :key="index" class="border-t border-gray-200">
              <td class="px-2 py-1 align-middle">
                <div v-if="!isReadOnly" class="v-select-filter" style="width: 100%; flex: none;">
                  <v-select
                    v-model="approver.user_id"
                    :options="users"
                    :reduce="u => u.id"
                    :get-option-label="userName"
                    placeholder="Выберите пользователя"
                  />
                </div>
                <span v-else>{{ approverName(approver) }}</span>
              </td>
              <td class="px-2 py-1 align-middle">
                <input
                  v-if="!isReadOnly"
                  v-model="approver.position_text"
                  type="text"
                  class="w-full border border-gray-300 rounded-lg px-3 text-sm h-[39px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span v-else>{{ approver.position_text || '—' }}</span>
              </td>
              <td class="px-2 py-1 align-middle">
                <span
                  v-if="approver.approved_at"
                  class="text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700"
                >
                  Одобрено {{ formatDate(approver.approved_at) }}
                </span>
                <span v-else class="text-gray-400">—</span>
              </td>
              <td class="px-2 py-1 align-middle text-center">
                <button
                  v-if="!isReadOnly"
                  type="button"
                  @click="removeApprover(index)"
                  class="text-red-600 hover:text-red-800"
                  title="Удалить строку"
                >
                  ✕
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="!isReadOnly" class="mt-3">
          <button type="button" @click="addApprover" class="text-sm text-blue-600 hover:underline">+ Добавить согласующего</button>
        </div>
      </section>

      <!-- Кнопки действий -->
      <div class="flex flex-wrap gap-2 justify-end">
        <button
          v-if="canSaveDraft"
          v-can-write
          type="button"
          @click="saveDraft"
          :disabled="saving"
          class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium disabled:opacity-50"
        >
          {{ saving ? 'Сохранение...' : (doc.status === 'draft' ? 'Сохранить как черновик' : 'Сохранить') }}
        </button>
        <button
          v-if="canSendForApproval"
          v-can-write
          type="button"
          @click="sendForApproval"
          :disabled="saving"
          class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded font-medium disabled:opacity-50"
        >
          Отправить на согласование
        </button>
        <button
          v-if="canApprove"
          v-can-write
          type="button"
          @click="approveDoc"
          :disabled="saving"
          class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium disabled:opacity-50"
        >
          Одобрить
        </button>
        <button
          v-if="canCancel"
          v-can-write
          type="button"
          @click="confirmCancelShow = true"
          :disabled="saving"
          class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium disabled:opacity-50"
        >
          Отменить
        </button>
      </div>
    </div>

    <!-- Модалка подтверждения отмены -->
    <div v-if="confirmCancelShow" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" @click.self="confirmCancelShow = false">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-md p-6">
        <h3 class="text-lg font-semibold mb-3">Отмена документа</h3>
        <p class="text-sm text-gray-700 mb-5">Отменить документ? После отмены форма станет доступна только для чтения.</p>
        <div class="flex justify-end gap-2">
          <button type="button" class="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300" @click="confirmCancelShow = false">Нет</button>
          <button type="button" class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium" @click="confirmCancelShow = false; cancelDoc()">Отменить документ</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { useAuthStore } from '@/stores/auth.js';
import ProductBindingSelector from '@/components/smkDesk/ProductBindingSelector.vue';
import { emptyBinding, bindingFromRow, bindingToPayload } from '@/components/smkDesk/productBinding.js';

// Централизованный маппинг «Ранга» в цвет заливки ячейки.
// Пороги и палитра — временные, калибруются позже в одном месте.
const RANK_THRESHOLDS = [
  { max: 4, color: '#bbf7d0' },        // низкий — зелёный
  { max: 9, color: '#fef08a' },        // средний — жёлтый
  { max: Infinity, color: '#fecaca' }, // высокий — красный
];
const RANK_TEXT_COLORS = {
  'низкий': '#bbf7d0', 'low': '#bbf7d0',
  'средний': '#fef08a', 'medium': '#fef08a',
  'высокий': '#fecaca', 'high': '#fecaca',
};
// Палитра ручного выбора цвета «Ранга» (значение в БД → цвет).
const MANUAL_RANK_COLORS = { green: '#bbf7d0', yellow: '#fef08a', red: '#fecaca' };

export default {
  components: { ProductBindingSelector },
  props: {
    moduleId: { type: [String, Number], required: true },
    riskId: { type: [String, Number], default: null },
    mode: { type: String, default: 'create' },
  },
  data() {
    return {
      docId: this.riskId ? Number(this.riskId) : null,
      localMode: this.mode,
      loading: false,
      saving: false,
      currentUser: null,
      creator: null,
      users: [],
      confirmCancelShow: false,
      doc: this.emptyDoc(),
      // Привязка к экземпляру у оценки рисков лежит в шапке документа, а не в строках
      binding: emptyBinding(),
      rows: [],
      approvers: [],
      manualProduct: false,
      productSelected: null,
      productResults: [],
      productLoading: false,
      productHasMore: false,
      productPage: 1,
      productQuery: '',
      verticalHeaderStyle: 'writing-mode: vertical-rl; transform: rotate(180deg); white-space: nowrap;',
      scoreOptions: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      rankPalette: [
        { value: '', color: '', title: 'Авто' },
        { value: 'green', color: '#bbf7d0', title: 'Зелёный' },
        { value: 'yellow', color: '#fef08a', title: 'Жёлтый' },
        { value: 'red', color: '#fecaca', title: 'Красный' },
      ],
    };
  },
  computed: {
    isReadOnly() {
      // approved и cancelled — форма только для чтения (правка запрещена)
      return ['approved', 'cancelled'].includes(this.doc.status);
    },
    assessmentDateDisplay() {
      // На странице создания (assessment_date ещё нет) показываем сегодняшнюю дату
      return this.doc.assessment_date ? this.formatDate(this.doc.assessment_date) : this.formatDate(new Date());
    },
    creatorDisplay() {
      return this.userName(this.creator);
    },
    // Из какого события создан документ — подпись приходит с бэкенда, ключ на экран не выводим
    originDisplay() {
      return this.doc.origin_label || 'Заведён вручную';
    },
    isCreator() {
      return this.currentUser && this.creator && this.currentUser.id === this.creator.id;
    },
    myApprover() {
      if (!this.currentUser) return null;
      return this.approvers.find(a => a.user_id === this.currentUser.id) || null;
    },
    canSaveDraft() {
      return !this.isReadOnly;
    },
    canSendForApproval() {
      return this.docId && this.doc.status === 'draft' && this.isCreator;
    },
    canApprove() {
      // Только если строка согласующего сохранена в БД (есть id) — иначе approve по несохранённой строке вернёт 403
      return this.doc.status === 'in_approval' && this.myApprover && this.myApprover.id && !this.myApprover.approved_at;
    },
    canCancel() {
      // Создатель может отменить из любого статуса, кроме уже отменённого
      return this.docId && this.doc.status !== 'cancelled' && this.isCreator;
    },
  },
  mounted() {
    this.currentUser = useAuthStore().currentUser;
    this.loadUsers();
    if (this.localMode === 'edit' && this.docId) {
      this.loadDocument();
    } else {
      this.initCreate();
    }
  },
  methods: {
    emptyDoc() {
      return {
        number: 'ОР-',
        assessment_date: null,
        affected_product_id: null,
        affected_product_name: '',
        purpose: '',
        status: 'draft',
        origin_label: '',
      };
    },
    emptyRow() {
      return {
        id: null,
        process: '', hazard: '', consequences: '',
        significance: '', probability: '', rank: '', rank_color: '',
        control_actions: '',
        re_significance: '', re_probability: '', re_rank: '', re_rank_color: '',
        additional_actions: '',
      };
    },
    initCreate() {
      this.creator = this.currentUser;
      this.doc = this.emptyDoc();
      this.binding = emptyBinding();
      this.rows = [];
      this.approvers = [];
      this.ensureFirstRow();
      this.ensureFirstApprover();
    },
    async loadUsers() {
      try {
        // Только пользователи с доступом к столу СМК — они могут согласовывать
        const { data } = await axios.get('/api/smk/risk-assessment/users');
        if (data?.success) {
          this.users = data.data || [];
        }
      } catch (e) {
        console.error('Не удалось загрузить список пользователей', e);
      }
    },
    async loadDocument() {
      this.loading = true;
      try {
        const { data } = await axios.get(`/api/smk/risk-assessment/${this.docId}`);
        if (data?.success) {
          this.applyDocument(data.data);
          this.ensureFirstRow();
          this.ensureFirstApprover();
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка загрузки документа');
      } finally {
        this.loading = false;
      }
    },
    applyDocument(d) {
      this.docId = d.id;
      this.localMode = 'edit';
      this.creator = d.creator || null;
      this.doc = {
        number: d.number || '',
        assessment_date: d.assessment_date || null,
        affected_product_id: d.affected_product_id || null,
        affected_product_name: d.affected_product_name || '',
        purpose: d.purpose || '',
        status: d.status,
        origin_label: d.origin_label || '',
      };
      // Привязка к экземпляру у ОР приходит в корне документа
      this.binding = bindingFromRow(d);
      this.manualProduct = !!(d.affected_product_name && !d.affected_product_id);
      this.productSelected = d.affected_product_id ? { id: d.affected_product_id, name: d.affected_product_name } : null;
      this.rows = (d.rows || []).map(r => ({
        id: r.id,
        process: r.process || '', hazard: r.hazard || '', consequences: r.consequences || '',
        significance: r.significance || '', probability: r.probability || '', rank: r.rank || '', rank_color: r.rank_color || '',
        control_actions: r.control_actions || '',
        re_significance: r.re_significance || '', re_probability: r.re_probability || '', re_rank: r.re_rank || '', re_rank_color: r.re_rank_color || '',
        additional_actions: r.additional_actions || '',
      }));
      this.approvers = (d.approvers || []).map(a => ({
        id: a.id,
        user_id: a.user_id,
        position_text: a.position_text || '',
        approved_at: a.approved_at || null,
      }));
    },
    buildPayload() {
      return {
        number: this.doc.number || null,
        affected_product_id: this.doc.affected_product_id || null,
        affected_product_name: this.doc.affected_product_name || null,
        purpose: this.doc.purpose || null,
        // Привязка к экземпляру — в корне тела запроса: строк по продуктам у ОР нет
        ...bindingToPayload(this.binding),
        rows: this.rows.map((r, idx) => ({
          id: r.id || undefined,
          process: r.process || null,
          hazard: r.hazard || null,
          consequences: r.consequences || null,
          significance: r.significance || null,
          probability: r.probability || null,
          rank: r.rank || null,
          rank_color: r.rank_color || null,
          control_actions: r.control_actions || null,
          re_significance: r.re_significance || null,
          re_probability: r.re_probability || null,
          re_rank: r.re_rank || null,
          re_rank_color: r.re_rank_color || null,
          additional_actions: r.additional_actions || null,
          sort: idx,
        })),
        approvers: this.approvers
          .filter(a => a.user_id)
          .map((a, idx) => ({
            id: a.id || undefined,
            user_id: a.user_id,
            position_text: a.position_text || null,
            sort: idx,
          })),
      };
    },
    async persist() {
      const payload = this.buildPayload();
      if (!this.docId) {
        const { data } = await axios.post('/api/smk/risk-assessment', payload);
        if (data?.success) {
          this.applyDocument(data.data);
          this.$router.replace({
            path: `/smk-desk/${this.moduleId}/risk-assessment/${this.docId}`,
            query: this.$route.query,
          });
          return true;
        }
        return false;
      }
      const { data } = await axios.put(`/api/smk/risk-assessment/${this.docId}`, payload);
      if (data?.success) {
        this.applyDocument(data.data);
        return true;
      }
      return false;
    },
    async saveDraft() {
      this.saving = true;
      try {
        if (await this.persist()) {
          this.notifySuccess('Документ сохранён');
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка сохранения');
      } finally {
        this.saving = false;
      }
    },
    async sendForApproval() {
      this.saving = true;
      try {
        if (!(await this.persist())) return;
        const { data } = await axios.post(`/api/smk/risk-assessment/${this.docId}/send-for-approval`);
        if (data?.success) {
          this.applyDocument(data.data);
          this.notifySuccess('Документ отправлен на согласование');
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка отправки на согласование');
      } finally {
        this.saving = false;
      }
    },
    async approveDoc() {
      this.saving = true;
      try {
        const { data } = await axios.post(`/api/smk/risk-assessment/${this.docId}/approve`);
        if (data?.success) {
          this.applyDocument(data.data);
          this.notifySuccess('Документ одобрен');
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка одобрения');
      } finally {
        this.saving = false;
      }
    },
    async cancelDoc() {
      this.saving = true;
      try {
        const { data } = await axios.post(`/api/smk/risk-assessment/${this.docId}/cancel`);
        if (data?.success) {
          this.applyDocument(data.data);
          this.notifySuccess('Документ отменён');
        }
      } catch (e) {
        this.notifyError(e, 'Ошибка отмены');
      } finally {
        this.saving = false;
      }
    },
    addRow() {
      this.rows.push(this.emptyRow());
    },
    ensureFirstRow() {
      // Если матрица пустая и форма редактируема — добавляем первую строку автоматически
      if (!this.isReadOnly && this.rows.length === 0) {
        this.addRow();
      }
    },
    removeRow(index) {
      this.rows.splice(index, 1);
    },
    addApprover() {
      this.approvers.push({ id: null, user_id: null, position_text: '', approved_at: null });
    },
    ensureFirstApprover() {
      // Если таблица согласующих пустая и форма редактируема — добавляем первую строку
      if (!this.isReadOnly && this.approvers.length === 0) {
        this.addApprover();
      }
    },
    removeApprover(index) {
      this.approvers.splice(index, 1);
    },
    // Цвет ячейки «Ранг»: ручной выбор (если задан) приоритетнее автозаливки по значению.
    cellRankColor(row, key) {
      const manual = row[key + '_color'];
      if (manual && MANUAL_RANK_COLORS[manual]) {
        return MANUAL_RANK_COLORS[manual];
      }
      return this.rankColor(row[key]);
    },
    // Заливка ячейки «Ранг» / «Повторная оценка → Ранг» по значению
    rankColor(value) {
      if (value === null || value === undefined || value === '') return '';
      const text = String(value).trim().toLowerCase();
      if (RANK_TEXT_COLORS[text]) return RANK_TEXT_COLORS[text];
      const num = parseFloat(text.replace(',', '.'));
      if (!isNaN(num)) {
        const band = RANK_THRESHOLDS.find(b => num <= b.max);
        return band ? band.color : '';
      }
      return '';
    },
    productLabel(option) {
      if (!option) return '';
      return option.sku ? `${option.sku} — ${option.name}` : option.name;
    },
    // Изделие выбрано — подставляем номенклатуру, если затронутый продукт ещё не указан
    onBindingProductPicked(picked) {
      if (!picked || this.manualProduct) return;
      if (picked.product_id && !this.doc.affected_product_id) {
        this.doc.affected_product_id = picked.product_id;
        this.doc.affected_product_name = picked.product_name || this.doc.affected_product_name;
        this.productSelected = { id: picked.product_id, name: picked.product_name };
      }
    },
    toggleManualProduct() {
      this.manualProduct = !this.manualProduct;
      this.productSelected = null;
      this.doc.affected_product_id = null;
      if (!this.manualProduct) {
        this.doc.affected_product_name = '';
      }
    },
    onProductSearch(query) {
      this.productQuery = query || '';
      this.productPage = 1;
      clearTimeout(this._productTimer);
      this._productTimer = setTimeout(() => this.fetchProducts(false), 300);
    },
    async fetchProducts(append) {
      this.productLoading = true;
      try {
        const { data } = await axios.post('/api/product/getProducts', {
          search: this.productQuery.trim(),
          page: this.productPage,
          per_page: 20,
        });
        const paginator = data?.data || {};
        const rows = paginator.data || [];
        this.productResults = append ? [...this.productResults, ...rows] : rows;
        const current = paginator.current_page || this.productPage;
        const last = paginator.last_page || current;
        this.productHasMore = current < last;
      } catch (e) {
        if (!append) this.productResults = [];
        this.productHasMore = false;
      } finally {
        this.productLoading = false;
      }
    },
    loadMoreProducts() {
      if (this.productLoading || !this.productHasMore) return;
      this.productPage += 1;
      this.fetchProducts(true);
    },
    onProductSelected(val) {
      this.productSelected = val || null;
      this.doc.affected_product_id = val && val.id ? val.id : null;
      this.doc.affected_product_name = val ? val.name : '';
    },
    goBack() {
      this.$router.push({
        path: `/smk-desk/${this.moduleId}`,
        query: { ...this.$route.query, tab: 'risk' },
      });
    },
    userName(user) {
      if (!user) return '—';
      const full = [user.last_name, user.first_name, user.patronymic].filter(Boolean).join(' ');
      return full || user.name || user.email || ('ID ' + user.id);
    },
    approverName(approver) {
      if (approver.user) return this.userName(approver.user);
      const u = this.users.find(x => x.id === approver.user_id);
      if (u) return this.userName(u);
      if (this.creator && this.creator.id === approver.user_id) return this.userName(this.creator);
      return approver.user_id ? ('ID ' + approver.user_id) : '—';
    },
    statusLabel(status) {
      return {
        draft: 'Новый',
        in_approval: 'В работе',
        approved: 'Согласован',
        cancelled: 'Отменён',
      }[status] || status;
    },
    statusClass(status) {
      return {
        draft: 'bg-gray-100 text-gray-700',
        in_approval: 'bg-yellow-100 text-yellow-700',
        approved: 'bg-green-100 text-green-700',
        cancelled: 'bg-red-100 text-red-700',
      }[status] || 'bg-gray-100 text-gray-700';
    },
    formatDate(value) {
      if (!value) return '—';
      const d = new Date(value);
      if (isNaN(d.getTime())) return '—';
      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
    },
    notifySuccess(message) {
      if (this.$root?.$toast?.success) {
        this.$root.$toast.success(message);
      }
    },
    notifyError(error, fallback) {
      const msg = error?.response?.data?.message || fallback || 'Ошибка';
      if (this.$root?.$toast?.error) {
        this.$root.$toast.error(msg);
      } else {
        alert(msg);
      }
    },
  },
};
</script>
